import { noop } from "lodash/fp";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import {
  DEFAULT_MEETING_RECAP_EMAIL_DISTRIBUTION,
  DEFAULT_PRE_READ_EMAIL_DISTRIBUTION,
} from "@app/common/constants/settings";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { UserDistributionSetting } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import TooltipWrapper from "@rui/TooltipWrapper";

import { SETTING_COPY } from "./constants";
import { Setting, SettingRow } from "./styles";

type EmailDistributionRecipient = UserDistributionSetting["sendTo"] | "no_one";

const EMAIL_DISTRIBUTION_OPTIONS: {
  value: EmailDistributionRecipient;
  label: string;
}[] = [
  { value: "participants_with_access", label: "Participants with access" },
  { value: "me", label: "Only me" },
  { value: "no_one", label: "No one" },
];

const ReportDistributionSettings = () => {
  const { userSettings, updateUserSettings } = useUserSettings();

  const { canEditSendMeetingRecap, canEditSendMeetingPreRead } =
    useFeatureAccess();

  const currentEmailMeetingReportRecipient: EmailDistributionRecipient =
    UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
      userSettings,
      { category: "meeting_report", provider: "email" }
    )?.sendTo ?? "no_one";

  const currentEmailPreReadRecipient: EmailDistributionRecipient =
    UserSettingsUtils.getEnabledUserDistributionSettingForCategoryAndProvider(
      userSettings,
      { category: "meeting_pre_read", provider: "email" }
    )?.sendTo ?? "no_one";

  const meetingRecapDistributionDisabledOpts =
    UserSettingsUtils.getSettingDisabledOpts({
      settingIsEditable: canEditSendMeetingRecap,
    });

  const meetingPreReadDistributionDisabledOpts =
    UserSettingsUtils.getSettingDisabledOpts({
      settingIsEditable: canEditSendMeetingPreRead,
    });

  const handleUpdateDistributionRecipient = (
    category: UserDistributionSetting["category"],
    value: EmailDistributionRecipient
  ) => {
    const defaultSetting =
      category === "meeting_report"
        ? DEFAULT_MEETING_RECAP_EMAIL_DISTRIBUTION
        : DEFAULT_PRE_READ_EMAIL_DISTRIBUTION;

    const existingSetting =
      UserSettingsUtils.getUserDistributionSettingForCategoryAndProvider(
        userSettings,
        { category, provider: "email" }
      ) ?? defaultSetting;

    const updatedDistributionSetting: UserDistributionSetting = {
      ...existingSetting,
      sendTo: value === "no_one" ? defaultSetting.sendTo : value,
      isEnabled: value !== "no_one",
    };

    updateUserSettings.mutate(
      { userDistributionSettings: [updatedDistributionSetting] },
      { onSuccess: noop } // don't show toast on success
    );
  };

  return (
    <SettingRow>
      <Setting {...SETTING_COPY.emailMeetingRecaps}>
        <TooltipWrapper
          title={meetingRecapDistributionDisabledOpts.tooltip}
          show={meetingRecapDistributionDisabledOpts.disabled}
          wrapperSx={{ width: "100%" }}
        >
          <Dropdown
            name="meeting-recap-email-recipient-select"
            disabled={meetingRecapDistributionDisabledOpts.disabled}
            selectedItem={getSelectedFromOptions(
              EMAIL_DISTRIBUTION_OPTIONS,
              currentEmailMeetingReportRecipient
            )}
            onChange={(event) =>
              handleUpdateDistributionRecipient(
                "meeting_report",
                event.target.value as EmailDistributionRecipient
              )
            }
            fullWidth
          >
            {EMAIL_DISTRIBUTION_OPTIONS.map(({ value, label }) => (
              <DropdownMenuItem key={value} value={value} label={label} />
            ))}
          </Dropdown>
        </TooltipWrapper>
      </Setting>
      <Setting
        className="review-settings__setting"
        {...SETTING_COPY.emailMeetingPreReads}
      >
        <TooltipWrapper
          title={meetingPreReadDistributionDisabledOpts.tooltip}
          show={meetingPreReadDistributionDisabledOpts.disabled}
          wrapperSx={{ width: "100%" }}
        >
          <Dropdown
            name="pre-read-email-recipient-select"
            disabled={meetingPreReadDistributionDisabledOpts.disabled}
            selectedItem={getSelectedFromOptions(
              EMAIL_DISTRIBUTION_OPTIONS,
              currentEmailPreReadRecipient
            )}
            onChange={(event) =>
              handleUpdateDistributionRecipient(
                "meeting_pre_read",
                event.target.value as EmailDistributionRecipient
              )
            }
            fullWidth
          >
            {EMAIL_DISTRIBUTION_OPTIONS.map(({ value, label }) => (
              <DropdownMenuItem key={value} value={value} label={label} />
            ))}
          </Dropdown>
        </TooltipWrapper>
      </Setting>
    </SettingRow>
  );
};

export default ReportDistributionSettings;
